import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";
import { Avatar, Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useNavigate } from "react-router-dom";

import CounsellorAuthContext from "../../context/CounsellorAuthContext";
import CounsellorSidebar from "./CounsellorSidebar";

const DASHBOARD_SCALE = 0.75;

const getNestedValue = (obj, paths, fallback = "") => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const CounsellorDashboardLayout = ({
  children,
  title = "Welcome to Counsellor Dashboard",
}) => {
  const navigate = useNavigate();
  const { counsellorUser, logoutCounsellor } = useContext(CounsellorAuthContext);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const counsellor = useMemo(() => counsellorUser?.counsellor || {}, [counsellorUser]);
  const userInfo = useMemo(() => counsellorUser?.userInfo || {}, [counsellorUser]);
  const counsellorName = getNestedValue(
    counsellor,
    ["name", "full_name", "username"],
    getNestedValue(userInfo, ["name", "full_name", "username"], "Counsellor")
  );
  const counsellorEmail = getNestedValue(
    counsellor,
    ["email"],
    getNestedValue(userInfo, ["email"], "")
  );
  const counsellorPhoto = getNestedValue(
    counsellor,
    ["photo", "image", "avatar", "profile_image", "profileImage"],
    getNestedValue(userInfo, ["photo", "image", "avatar", "profile_image", "profileImage"], "")
  );

  const handleLogout = () => {
    setProfileAnchor(null);
    logoutCounsellor();
    navigate("/counsellor-portal/login");
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#FAFAFC",
      }}
    >
      <Box
        sx={{
          width: `calc(100vw / ${DASHBOARD_SCALE})`,
          height: `calc(100vh / ${DASHBOARD_SCALE})`,
          transform: `scale(${DASHBOARD_SCALE})`,
          transformOrigin: "top left",
          display: "flex",
          bgcolor: "#FAFAFC",
          fontFamily: '"Inter", "Poppins", sans-serif',
        }}
      >
        <CounsellorSidebar onLogout={handleLogout} />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            overflow: "auto",
            px: 3,
            py: 3.25,
            bgcolor: "#FAFAFC",
          }}
        >
          <Box
            sx={{
              minHeight: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: "1px solid #EFEFF4",
              mb: 3,
              pb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725" }}>
              {title}
            </Typography>

            <Box
              component="button"
              type="button"
              onClick={(event) => setProfileAnchor(event.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: "1px solid #EFEFF4",
                bgcolor: "#fff",
                borderRadius: "10px",
                px: 1.2,
                py: 0.8,
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                "&:hover": { bgcolor: "#F8F9FC" },
              }}
            >
              <Avatar
                src={counsellorPhoto || undefined}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "#2D315F",
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                {!counsellorPhoto ? counsellorName?.[0]?.toUpperCase() : null}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#171725" }}>
                  {counsellorName}
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#92929D" }}>
                  Counsellor
                </Typography>
              </Box>

              <KeyboardArrowDownIcon sx={{ color: "#111827", fontSize: 20 }} />
            </Box>

            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={() => setProfileAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 240,
                  borderRadius: "10px",
                  border: "1px solid #EFEFF4",
                  boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)",
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725" }}>
                  {counsellorName}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#92929D", mt: 0.3 }}>
                  {counsellorEmail}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  minHeight: 42,
                  gap: 1,
                  color: "#B42318",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {children}
        </Box>
      </Box>
    </Box>
  );
};

CounsellorDashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
};

export default CounsellorDashboardLayout;
